import styles from './CreateBinder.scss';
import formStyles from 'styles/forms.scss';
import './external.scss';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Link } from 'react-router';
import { renderSingleDatePicker } from 'common/components/Form/Form';
import { connect } from 'react-redux';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { Conditional } from 'common/components/layout';
import { isIntegrationToggleEnabled, partnerName, subscribeToConnexusStudy, unsubscribeToConnexusStudy } from 'common/actions/integrationActions';
import { loadBinderTemplates, submitCreateBinder } from 'overview/actions';
import { loadBinder, submitSaveBinderProperties, loadBinderStatusOptions } from 'binder/actions';
import lodash from 'lodash';
import { replace } from 'react-router-redux';
import LoadingComponent from 'common/permissions/components/LoadingComponent';

const validate = values => {
    const errors = {};
    if(!values.binderTemplateId) {
        errors.binderTemplateId = '* Required';
    }
    if (!values.title) {
        errors.title = '* Required';
    }
    if (!values.description) {
        errors.description = '* Required';
    }
    if (!values.status) {
        errors.status = '* Required';
    }
    return errors;
};

const renderDescriptionTextarea = ({name, input, label, type, disabled, required, meta: { touched, error }}) => {
    const inputStyle = touched && error ? styles.descriptionError : styles.description;
    const requiredClass = required ? formStyles.required : '';
    return (
        <div className={styles.formElement} data-cy="CreateBinder">
            <label className={requiredClass} htmlFor={name} data-cy="CreateBinder-name">
                {label}
            </label>
            <div className={styles.formInput}>
                <textarea {...input}
                    disabled={disabled || false}
                    id={name}
                    type={type}
                    placeholder={label}
                    autoComplete="off"
                    rows="5"
                    maxLength="1000"
                    className={inputStyle}
                    data-cy="CreateBinder-textarea"
                />
                { touched && error && <span className={styles.formError} data-cy="CreateBinder-error">{error}</span> }
            </div>
        </div>    

    );
};

const renderInputField = ({name, input, label, type, meta: { touched, error }, placeholder}) => {
    const inputStyle = touched && error ? styles.inputTextBoxError : styles.inputTextBox;
    return (
        <div className={styles.formElement} data-cy="CreateBinder-renderInputField">
            <label htmlFor={name} data-cy="CreateBinder-name">
                {label}
            </label>
            <div className={styles.formInput}>
                <input {...input}
                    data-cy={'CreateBinder-' + name}
                    id={name}
                    type={type}
                    placeholder={placeholder || label}
                    autoComplete="off"
                    className={inputStyle}/>
                { touched && error && <span className={styles.formError}>{error}</span> }
            </div>
        </div>
    );
};

const renderCheckbox = ({name, defaultChecked, input}) => {
    return(
        <div className={styles.toggleContent}>
            <input
                {...input}
                className={styles.toggleCheckbox}
                type='checkbox'
                name={name}
                id='binderEventToggle'
                defaultChecked={defaultChecked}
                data-cy="CreateBinder-binderEventToggle"
            />
            <label 
                htmlFor='binderEventToggle'
            >Turn on Events</label>
        </div>
    );
};

const renderSelectField = ({name, input, label, options, meta: { touched, error }}) => {
    const selectStyle = touched && error ? formStyles.dropdownListError : formStyles.dropdownList;
    const optionElements = Array.isArray(options)
        ? options.map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)
        : null;
    return (
        <div className={styles.formElement} data-cy="CreateBinder-renderSelectField">
            <label htmlFor={name} data-cy="CreateBinder-label">
                { label }
            </label>
            <div className={styles.formInput}>
                <select {...input} id={name} className={selectStyle} data-cy={'CreateBinder-' + name}>
                    <option value=""></option>
                    { optionElements }
                </select>
                { touched && error && <span className={styles.formError} data-cy={'CreateBinder-' + error}>{error}</span> }
            </div>
        </div>
    );

};

const renderConnexusInputField = ({
    name,
    input,
    label,
    meta: { touched, error },
    textDisabled=false,
    loading=false,
    placeholder,
}) => {
    const inputStyle = touched && error ? styles.inputTextBoxError : styles.inputTextBox;
    return (
        <div className={styles.formElement} data-cy="CreateBinder-renderConnexusInputField">
            <label htmlFor={name} data-cy="CreateBinder-label">
                {label}
            </label>
            <div className={styles.connexusFormInput}>
                <input {...input}
                    id={name}
                    type='number'
                    placeholder={placeholder || label}
                    autoComplete="off"
                    disabled={loading || textDisabled}
                    className={inputStyle}
                    data-cy={'CreateBinder-' + name}/>
                { touched && error && <span className={styles.formError} data-cy="CreateBinder-formError">{error}</span> }
            </div>
        </div>
    );
};

export class CreateBinder extends Component {

    componentDidUpdate(prevProps) {
        const { isEdit, initialized, binder } = prevProps;
        if (isEdit && !initialized && !lodash.isEmpty(binder)) {
            const renewalDate = binder.renewalDate ? binder.renewalDate : null;
            this.props.initialize({
                ...binder,
                renewalDate,
                renewalDateFocused: false
            });
        }

        if (!isEdit && !initialized) {
            this.props.initialize({
                renewalDate: null,
                renewalDateFocused: false
            });
        }
    }

    renderConnexusFields () {
        const {
            binder,
            subscribing,
            connexusStudyId,
            connexusSiteId,
        } = this.props;

        const connexusSubscribed = binder.partnerMetadata && binder.partnerMetadata.connexusSubscribed ?
            binder.partnerMetadata.connexusSubscribed : false;

        return (
            <Conditional condition={isIntegrationToggleEnabled(binder.integrations, partnerName.CONNEXUS)}>
                <div className={styles.connexusFormWrapper} data-cy="CreateBinder-Conditional">
                    <div className={styles.connexusFormInputs}>
                        <Field
                            name="partnerMetadata.connexusStudyId"
                            label="Connexus Integration Details"
                            placeholder="Connexus Integration Study ID"
                            textDisabled={connexusSubscribed}
                            loading={subscribing}
                            component={renderConnexusInputField}
                        />
                        <Field
                            name="partnerMetadata.connexusSiteId"
                            placeholder="Connexus Integration Site ID"
                            textDisabled={connexusSubscribed}
                            loading={subscribing}
                            component={renderConnexusInputField}
                        />
                    </div>
                    <button
                        className={styles.connexusFormButton}
                        disabled={
                            subscribing ||
                                !connexusStudyId ||
                                !connexusStudyId.length ||
                                !connexusSiteId ||
                                !connexusSiteId.length
                        }
                        data-cy="CreateBinder-onClick"
                        onClick={(e) => {
                            e.preventDefault();
                            this.props.onSubscribeToConnexusStudy(
                                { 
                                    binder: {
                                        id: binder.id,
                                        layout: binder.layout,
                                        title: binder.title,
                                        partnerMetadata: {
                                            ...binder.partnerMetadata,
                                            connexusStudyId,
                                            connexusSiteId,
                                            connexusSubscribed
                                        }
                                    }
                                });
                        }}
                    >
                        {subscribing ?
                            `${connexusSubscribed ? 'Unsubscribing' :'Subscribing'}`:
                            `${connexusSubscribed ? 'Unsubscribe' :'Subscribe'}`}
                    </button>
                </div>
            </Conditional>
        );
    }

    render(){
        const { 
            handleSubmit,
            pristine,
            reset,
            submitting,
            invalid,
            templates,
            isEdit,
            binder,
            binderStatusOptions
        } = this.props;

        if (lodash.isEmpty(templates)) {
            return (
                <div className={styles.page} data-cy="CreateBinder-render">
                    <div className={styles.pageHeader}>
                        <Link className={styles.back} to={isEdit ? `/binder/${binder.id}` : '/binders'} data-cy="CreateBinder-binders">
                            { '< back' }
                        </Link>
                        <span className={styles.heading} data-cy="CreateBinder-isEdit">
                            { isEdit ? 'Edit Binder' : 'Create Binder' }
                        </span>
                    </div>
                    <div className={styles.container}>
                        <div className={styles.createBinderContent}>
                            <span className={styles.noBinderTemplates}>You do not have access to any binder templates to create. Please contact an administrator.</span>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <form className={styles.form} onSubmit={handleSubmit} data-cy="CreateBinder-form">
                { !isEdit &&
                    <Field name="binderTemplateId" component={renderSelectField} label="Binder Template" options={templates} />
                }
                <Field name="title" type="text" label="Title" component={renderInputField} />
                <Field name="description" type="text" label="Description" component={renderDescriptionTextarea} />
                <Field name="trialType" type="text" label="Trial Type" component={renderInputField} />
                <Field name="primaryInvestigator" type="text" label="Principal Investigator" component={renderInputField} />
                <Field name="irbNumber" type="text" label="IRB Number" component={renderInputField} />
                <Field name="contact" type="text" label="Contact" component={renderInputField} />
                <Field name="trialId" type="text" label="Trial ID" component={renderInputField} />
                <Field name="departmentSiteDg" type="text" label="Department / Site / DG" component={renderInputField} />
                <Field name="ctGovNumber" type="text" label="ClinicalTrials.gov Number" component={renderInputField} />
                <Field name="sponsorCro" type="text" label="Sponsor / CRO" component={renderInputField} />
                <Field name="renewalDate"
                    label="Renewal Date"
                    component={renderSingleDatePicker}
                    className={styles.formElement}
                    dateWrapperClassName={styles.formInput}
                    noTimezone={true}
                />
                {
                    isEdit && binder.status !== 'NEW' &&
                    <Field name="status" component={renderSelectField} label="Status" options={binderStatusOptions}/>
                }
                <Conditional condition={isIntegrationToggleEnabled(binder.integrations, partnerName.VELOS)}>
                    <Field
                        name="partnerMetadata.velosStudyId"
                        type="text"
                        label={['Velos Integration',<br/>,'Study ID']}
                        placeholder="Velos Integration Study ID"
                        component={renderInputField}
                    />
                </Conditional>
                {this.renderConnexusFields()}
                <Field name="eventsTabEnabled" defaultChecked={binder.eventsTabEnabled} component={renderCheckbox} />
                <div className={styles.formElement}>
                    <button type="submit" className={formStyles.button}
                        data-cy="CreateBinder-submit"
                        disabled={submitting || invalid}>
                        { isEdit ? 'Save Binder' : 'Create Binder'}
                    </button>
                    <button type="button" className={formStyles.buttonReset} onClick={reset}
                        disabled={pristine || submitting} data-cy="CreateBinder-button">
                        Reset values
                    </button>
                </div>
            </form>
        );
    }
}

const CreateBinderForm = reduxForm({
    form: 'createbinder',
    validate
})(CreateBinder);

class CreateBinderFormContainer extends Component {

    constructor() {
        super();
        this.state = {
            subscribing: false
        };
    }

    onSubmit(values) {
        if (this.props.binderId) {
            return this.props.submitSaveBinderProperties(values);
        }

        return this.props.submitCreateBinder(values);
    }

    async onSubscribeToConnexusStudy({ binder }) {
        this.setState({ subscribing: true });
        if (binder.partnerMetadata && binder.partnerMetadata.connexusSubscribed) {
            await this.props.unsubscribeToConnexusStudy({ binder });
        } else {
            await this.props.subscribeToConnexusStudy({ binder });
        }
        this.setState({ subscribing: false });
    }

    shouldRedirectToSetup() {
        const { createBinderInProgress, createBinderSuccess } = this.props;
        return !createBinderInProgress && createBinderSuccess;
    }

    componentDidUpdate() {
        if (this.shouldRedirectToSetup()) {
            const { id } = this.props.binder;
            this.props.replace(`/binder/${id}/setup`);
        }
    }

    componentDidMount() {
        this.props.loadBinderTemplates();

        if (this.props.binderId) {
            this.props.loadBinder(this.props.binderId);
        }

        this.props.loadBinderStatusOptions();
    }

    render() {
        const {
            isComplionAdmin,
            templates,
            binderId,
            binder,
            isEdit,
            createBinderInProgress,
            binderStatusOptions,
            connexusStudyId,
            connexusSiteId,
        } = this.props;

        const filteredBinderStatusOptions = isComplionAdmin
            ? binderStatusOptions
            : binderStatusOptions.filter(option => option.id !== 'REMOVED');

        return (
            <div className={styles.page} data-cy="CreateBinder-render">
                <div className={styles.pageHeader}>
                    <Link className={styles.back} to={isEdit ? `/binder/${binder.id}` : '/binders'}>
                        { '< back' }
                    </Link>
                    <span className={styles.heading}>
                        { isEdit ? 'Edit Binder' : 'Create Binder' }
                    </span>
                </div>
                { !isEdit && createBinderInProgress &&
                    <div className={styles.inProgressContainer}>
                        <p className={styles.inProgressText}>
                            We are creating the binder. This may take up to a minute.
                        </p>
                        <div className={styles.createSpinner}>
                            <LoadingComponent text="Creating" />
                        </div>
                    </div>

                }
                { !createBinderInProgress &&
                    <CreateBinderForm
                        onSubmit={this.onSubmit.bind(this)}
                        onSubscribeToConnexusStudy={({ binder }) => this.onSubscribeToConnexusStudy({ binder })}
                        subscribing={this.state.subscribing}
                        isEdit={binderId ? true : false}
                        binder={binder}
                        templates={templates}
                        binderStatusOptions={filteredBinderStatusOptions}
                        connexusStudyId={connexusStudyId}
                        connexusSiteId={connexusSiteId}
                    />
                }
            </div>
        );
    }
}

CreateBinderFormContainer.contextTypes = {
    router: PropTypes.object.isRequired
};

export default connect(
    (state, props) => {
        const selector = formValueSelector('createbinder');
        return {
            isComplionAdmin: lodash.get(state, 'me.systemPermissions.complionAdmin'),
            templates: lodash.get(state, 'me.binderTemplates'),
            binderId: props.params.id,
            isEdit: Boolean(props.params.id),
            binder: lodash.get(state, 'binder', {}),
            createBinderSuccess: lodash.get(state, 'binder.createBinderSuccess', false),
            createBinderInProgress: lodash.get(state, 'binder.createBinderInProgress', false),
            binderStatusOptions: lodash.get(state, 'binderStatusOptions', []),
            connexusStudyId: selector(state, 'partnerMetadata.connexusStudyId'),
            connexusSiteId: selector(state, 'partnerMetadata.connexusSiteId'),
        };
    },
    {
        submitCreateBinder,
        submitSaveBinderProperties,
        loadBinderStatusOptions,
        loadBinderTemplates,
        loadBinder,
        replace,
        subscribeToConnexusStudy,
        unsubscribeToConnexusStudy,
    }
)(CreateBinderFormContainer);
